import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import queryString from 'query-string-for-all';

import type { AppContextValue } from 'src/components/app-context';
import { AppContext } from 'src/components/app-context';
import { ViewportContext } from 'src/components/viewport';
import { CampaignCardContextContainer } from '../campaign-card/campaign-card-context';

import { Axis } from '@prisjakt/ui';
import { Hideable } from '@prisjakt/ui/hideable';
import { NumberPagination } from 'src/components/number-pagination';
import { ScrollLink } from 'src/components/scroll';
import { CampaignCard } from '../campaign-card';
import { Translate } from 'src/components/translate';
import { Ads } from 'src/areas/product-collection/components/ads';
import type { Option } from 'src/components/filter-dropdown';
import { FilterDropdown } from 'src/components/filter-dropdown';
import { ProductsHeader } from 'src/components/products-header';
import Placeholder from '../../../../components/placeholder';

import queryBuilder from '../../query';
import { campaignItemClick } from 'src/areas/campaign/actions';
import { trackSortOrderChange } from '../../tracking';
import Search from '../search';
import { useElasticFetch } from '../data/use-with-elastic';

import * as S from './styles';
import { IconMagnifier } from '@prisjakt/ui/icon/magnifier';

import { productPriceClick } from 'src/areas/product/actions/product';
import {
  InternalNavigationScope,
  trackInternalNavigationClick,
} from 'src/globals/tracking/tracking';
import {
  TrackClickScope,
  trackOfferClickWithRevenue,
} from 'src/globals/tracking/conversion';

import { PrimaryButton } from '@prisjakt/ui/button/variants';
import type { StockStatus } from 'src/types';
import { LayoutMode } from 'src/components/layout-buttons/types';
import type { RouterParams } from '../../types';
import type { Source } from './types';
import { SortOptionValueEnum } from './types';

const DEFAULT_SIZE = 24;
const DEFAULT_SORT = SortOptionValueEnum.RANK_DESC;
const RATING_OPTION_KEYS = ['productRating', 'storeRating'];

const ADS_LAYOUT = {
  [LayoutMode.GRID]: {
    panorama_extra: { show: true, row: 3 },
    dubbelpanorama1: { show: false, row: 8 },
    dubbelpanorama2: { show: true, row: 4 },
  },
};

const buildProductProps = (
  _source: Source,
  renderMode: AppContextValue['renderMode'],
  market: AppContextValue['market']
) => {
  let link =
    market.useClassicUrls && market.domain
      ? _source.product.web_uri.split(market.domain)[1] // [<siteDomain>, '/product.php?p=xyz']
      : _source.product.semantic_id;

  if (renderMode === 'desktop' && market.domain) {
    link = _source.product.web_uri.split(market.domain)[1];
  }

  if (renderMode === 'native') {
    link = _source.product.web_uri;
  }

  const product = {
    id: _source.product.id,
    name: _source.product.name,
    rating: (_source.product.user_reviews.rating || 0) / 2, // Convert to 5-scale to fit the Ratings component
    count: _source.product.user_reviews.count,
    price: _source.price?.offer,
    link,
    mode: 'grid',
    category: {
      name: _source.product.category.name,
    },
    image: _source.product.media.product_images.first
      ? _source.product.media.product_images.first[280]
      : null,
    stockStatus: _source.product.stock_status as StockStatus,
  };

  return product;
};

type Props = {
  data?: {
    hits?: {
      hits: { _source: Source; _id?: string }[];
      total?: number;
    };
  };
  onChange: (item: {
    id: string;
    value: string | number;
    deleteOffset?: boolean;
  }) => void;
  onClear: () => void;
  showPagination?: boolean;
  showProductsHeader?: boolean;
  rangeOptions: {
    key?: string;
    field?: string;
    translation?: string;
    showBars?: boolean;
  }[];
  router: RouterParams;
  stores?: string[];
  size?: number;
  campaignId: number;
};

export const Deals = (props: Props) => {
  const {
    onChange,
    onClear,
    router,
    size = DEFAULT_SIZE,
    showPagination = true,
    showProductsHeader = true,
    campaignId,
  } = props;

  const { market, renderMode, t } = useContext(AppContext);
  const { isMobile, isTablet } = useContext(ViewportContext);
  const { search } = useLocation();
  const dispatch = useDispatch();

  const [showSearchField, setShowSearchField] = useState<boolean>(false);

  const [currentQuery, setCurrentQuery] = useState({});
  const [lastSearchQuery, setLastSearchQuery] = useState('');

  const { fetching, data } = useElasticFetch({
    query: currentQuery,
    // eslint-disable-next-line object-shorthand
    campaignId: campaignId,
    timeout: router.query === lastSearchQuery ? 0 : 500,
  });

  useEffect(() => {
    setCurrentQuery(buildQuery(null, props));
  }, [router]);

  useEffect(() => {
    const routerQuery = router.query || '';
    if (routerQuery === lastSearchQuery) {
      return;
    }
    setLastSearchQuery(routerQuery);
  }, [data]);

  const sortOptions: { title: string; value: string }[] = [
    {
      title: t('campaigns', `sort.${SortOptionValueEnum.RANK_DESC}`),
      value: SortOptionValueEnum.RANK_DESC,
    },
    {
      title: t('campaigns', `sort.${SortOptionValueEnum.PRODUCT_NAME_KEYWORD}`),
      value: SortOptionValueEnum.PRODUCT_NAME_KEYWORD,
    },
    {
      title: t(
        'campaigns',
        `sort.${SortOptionValueEnum.PRODUCT_POPULARITY_TOTAL}`
      ),
      value: SortOptionValueEnum.PRODUCT_POPULARITY_TOTAL,
    },
    {
      title: t('campaigns', `sort.${SortOptionValueEnum.PRICE_OFFER_ASC}`),
      value: SortOptionValueEnum.PRICE_OFFER_ASC,
    },
    {
      title: t('campaigns', `sort.${SortOptionValueEnum.PRICE_OFFER_DESC}`),
      value: SortOptionValueEnum.PRICE_OFFER_DESC,
    },
    {
      title: t(
        'campaigns',
        `sort.${SortOptionValueEnum.PRODUCT_USER_REVIEWS_RATING_DESC}`
      ),
      value: SortOptionValueEnum.PRODUCT_USER_REVIEWS_RATING_DESC,
    },
    {
      title: t(
        'campaigns',
        `sort.${SortOptionValueEnum.PRICE_DIFF_PERCENTAGE_DESC}`
      ),
      value: SortOptionValueEnum.PRICE_DIFF_PERCENTAGE_DESC,
    },
    ...(router.sort === SortOptionValueEnum.RELEVANCE
      ? [
          {
            title: t('campaigns', `sort.${SortOptionValueEnum.RELEVANCE}`),
            value: SortOptionValueEnum.RELEVANCE,
          },
        ]
      : []),
  ];

  const addStickyOffset = useCallback(() => {
    if (window.innerWidth < 640) {
      return 50;
    }

    return 70;
  }, []);

  const generatePaginationUrl = useCallback(
    (pageNumber: number) =>
      `?${queryString.stringify({
        ...queryString.parse(search.substring(1)),
        offset: pageNumber > 1 ? (pageNumber - 1) * size : undefined,
      })}`,
    [search, size]
  );

  const currentSort: string =
    sortOptions.find(item => router.sort === item.value)?.value || DEFAULT_SORT;

  const currentPage =
    typeof router.offset === 'number' && typeof size === 'number'
      ? Math.floor(router.offset / size) + 1
      : 1;

  const handleSortChange = (option: Option) => {
    if (option.value) {
      trackSortOrderChange(option.value);
      onChange({
        id: 'sort',
        value: option.value,
      });
    }
  };

  const productsCountText =
    data.hits?.total && data.hits?.total > 0
      ? t('campaigns', 'deals.n-products-subtitle', {
          n: data.hits.total,
        })
      : undefined;

  return (
    <>
      {showProductsHeader && (
        <ProductsHeader
          displayOptions={
            isMobile || isTablet ? (
              <button
                data-test="ShowSearchButton"
                type="button"
                onClick={() => setShowSearchField((state: boolean) => !state)}
              >
                <IconMagnifier size={27} color="maybeBlue700" />
              </button>
            ) : null
          }
          sortOptions={
            <FilterDropdown
              defaultSelected={sortOptions.find(
                option => option.value === currentSort
              )}
              title={isMobile || isTablet ? '' : t('common', 'sort.title')}
              options={sortOptions}
              onFilterChange={handleSortChange}
            />
          }
          subtitle={isMobile || isTablet ? undefined : productsCountText}
          title={
            isMobile || isTablet ? undefined : t('campaigns', 'products.title')
          }
        >
          {isMobile || isTablet ? (
            <>
              <Hideable
                collapse={Axis.Y}
                visible={showSearchField}
                renderWhenHidden
              >
                <S.SearchField show={showSearchField} data-test="SearchField">
                  <Search
                    query={router.query}
                    onSearch={(item: { id: string; value: string }) =>
                      onChange(item)
                    }
                  />
                </S.SearchField>
              </Hideable>
              <S.ProductCount data-test="ProductCount">
                {productsCountText}
              </S.ProductCount>
            </>
          ) : null}
        </ProductsHeader>
      )}

      {!fetching && (!data.hits?.hits || data.hits.hits.length === 0) && (
        <S.Empty>
          <Translate domain="campaigns">item.no-results.md</Translate>
          <PrimaryButton onClick={onClear}>
            <Translate domain="campaigns">link.view-all</Translate>
          </PrimaryButton>
        </S.Empty>
      )}

      {data.hits?.hits.length ? (
        <S.DealsGrid
          className="deals-grid"
          offers={data.hits?.hits.map(({ _source, _id }, index) => {
            const product = buildProductProps(_source, renderMode, market);
            const price = _source.price;

            const gridCardProps = {
              category: product.category,
              diffPercentage: Math.round(_source.price?.diff_percentage || 0),
              externalLink: _source.external_uri.replace(
                '&api_client=1',
                '&api_client=1331'
              ),
              image: product.image || '',
              internalLink: product.link,
              name: product.name,
              handleCardOnClick: () => {
                dispatch(campaignItemClick(_source));
                trackInternalNavigationClick({
                  scope: InternalNavigationScope.CAMPAIGN_PRODUCT_CARD,
                  nextPage: product.link,
                  index,
                });
              },
              handleClickOut: () => {
                dispatch(
                  productPriceClick(
                    product.id,
                    typeof product.price === 'string'
                      ? parseInt(product.price, 10)
                      : product.price,
                    true,
                    '',
                    false,
                    'Deals'
                  )
                );
                if (price) {
                  trackOfferClickWithRevenue({
                    scope: TrackClickScope.CAMPAIGN_LIST_ITEM,
                    offerId: String(price?.id),
                    productId: product.id,
                  });
                }
              },
              price: {
                id: _source.price?.id,
                price:
                  typeof product.price === 'string'
                    ? parseInt(product.price, 10)
                    : product.price,
                previousPrice: price?.display?.previousPrice,
                previousPriceText: price?.days_ago_compare
                  ? t('campaigns', 'deals.price-n-days-ago', {
                      /* The date that days_ago_compare is based on is taken from just after midnight,
                                so from a user perspective it makes more sense to say that the comparison is
                                with the price from the day *before* that. A bit confusing, but that's how
                                it seems to work... */
                      count: price.days_ago_compare + 1,
                    })
                  : undefined,
              },
              rating: product.rating,
              reviewsCount: product.count,
              stockStatus: product.stockStatus,
              store: _source.store
                ? {
                    id: _source.store.id,
                    name: _source.store.name,
                    hasLogo: !!_source.store.logo,
                    featured: !!_source.store.featured,
                  }
                : undefined,
              storeCount: _source.product.price?.count_regular,
            };

            return (
              <CampaignCardContextContainer key={_id} {...gridCardProps}>
                <CampaignCard />
              </CampaignCardContextContainer>
            );
          })}
        >
          <Ads
            offersLength={data.hits?.hits.length || 0}
            layout={LayoutMode.GRID}
            layoutConfig={ADS_LAYOUT}
            scaleBase="deals-grid"
            async
          />
        </S.DealsGrid>
      ) : null}

      {fetching && (
        <Placeholder height={450} amount={12} rows={8} mediaPosition={4} />
      )}

      {showPagination && (
        <ScrollLink
          to="CampaignHeaderAd"
          shouldScroll={({ tagName }: { tagName: string }) => tagName === 'A'}
          animate={false}
          calculateOffset={addStickyOffset}
        >
          <NumberPagination
            currentPage={currentPage}
            totalPages={Math.ceil((data.hits?.total || 0) / size)}
            linkToPage={generatePaginationUrl}
            onClick={item => {
              onChange({
                id: 'offset',
                value: item.pageNumber > 1 ? (item.pageNumber - 1) * size : 0,
                deleteOffset: false,
              });
            }}
          />
        </ScrollLink>
      )}
    </>
  );
};

/* eslint-disable camelcase */
type BodyProps = {
  _source: {
    include: string[];
  };
  from: number;
  size: number;
  sort: (
    | string
    | {
        [x: string]: {
          order?: string;
          unmapped_type: string;
        };
      }
  )[];
  query: {
    bool: {
      filter?: { bool: { must: { range: Record<string, any> }[] } };
      must: (
        | {
            bool: {
              should: {
                term: {
                  'product.category.path.id': string;
                };
              }[];
            };
          }
        | {
            bool: {
              should: {
                term: {
                  'store.id': string;
                };
              }[];
            };
          }
      )[];
    };
  };
};

const buildQuery = (state: unknown, props: Props) => {
  const [sort, order] = (props.router.sort || DEFAULT_SORT).split('|');
  const offset = props.router.offset || 0;
  const { categories, query, filterRanges } = props.router;
  const { size } = props;
  let stores = props.router.stores;

  if (props.stores && props.stores.length) {
    // Stores can come both from the url or as props. Props have the higher priority atm in order to prevent weird behavior.
    stores = props.stores;
  }

  const body = {
    _source: {
      include: [
        'price.display.*',
        'price.diff_percentage',
        'price.days_ago_compare',
        'price.id',
        'price.offer',
        'price.offer_incl_shipping',
        'product.id',
        'product.semantic_id',
        'product.web_uri',
        'product.mobile_web_uri',
        'product.name',
        'product.media',
        'product.price.count',
        'product.price.count_regular',
        'product.statistics.sparklines',
        'product.category.name',
        'product.user_reviews.rating',
        'product.user_reviews.count',
        'product.stock_status',
        'brand.logo',
        'brand.name',
        'store.name',
        'store.id',
        'store.logo',
        'store.featured',
        'external_uri',
      ],
    },
    from: offset,
    size: size || DEFAULT_SIZE,
  } as BodyProps;

  const selectionGroups = [];

  // Use the score first if the current sort is relevance
  body.sort =
    sort === SortOptionValueEnum.RELEVANCE
      ? ['_score']
      : [
          {
            [sort]: {
              order: order || 'asc',
              unmapped_type:
                sort === SortOptionValueEnum.PRODUCT_NAME_KEYWORD
                  ? 'keyword'
                  : 'long',
            },
          },
          '_score',
        ];

  if (sort === DEFAULT_SORT.split('|')[0]) {
    body.sort.push({
      [SortOptionValueEnum.PRODUCT_POPULARITY_TOTAL]: { unmapped_type: 'long' },
    });
  }

  if (categories?.length) {
    selectionGroups.push({
      bool: {
        should: categories.map(value => ({
          term: { 'product.category.path.id': value },
        })),
      },
    });
  }

  if (stores) {
    selectionGroups.push({
      bool: {
        should: stores.map(value => ({
          term: { 'store.id': value },
        })),
      },
    });
  }

  body.query = {
    bool: {
      must: selectionGroups,
    },
  };

  body.query.bool.filter = { bool: { must: [] } };
  props.rangeOptions.forEach(option => {
    if (filterRanges && option.key && filterRanges[option.key]) {
      const filter = { range: {} };
      if (option.field)
        filter.range[option.field] = {
          gte: RATING_OPTION_KEYS.includes(option.key)
            ? filterRanges[option.key].from * 2 // Ratings are between 1-10
            : filterRanges[option.key].from,
          lte: RATING_OPTION_KEYS.includes(option.key)
            ? filterRanges[option.key].to * 2 // Ratings are between 1-10
            : filterRanges[option.key].to,
        };

      if (body.query.bool.filter) body.query.bool.filter.bool.must.push(filter);
    }
  });

  if (query) {
    body.query.bool.must = body.query.bool.must.concat(queryBuilder(query));
  }

  return body;
};
